class PaginationChange extends HTMLElement {
  constructor () {
    super()

    this.elements = {
      nextTrigger: this.querySelector(
        '[data-pagination-change-el="next-trigger"]',
      ),
      prevTrigger: this.querySelector(
        '[data-pagination-change-el="prev-trigger"]',
      ),
      progressBar: this.querySelector('[data-pagination-change-el="progress"]'),
    }

    this.state = {
      root: window.theme.tagalys.results.stateLayer.state,
    }

    // Store bound handlers for cleanup
    this._boundHandlers = {
      handleProductsUpdated: products => {
        if (products.length > 0) {
          this.render()
        } else {
          this.ariaHidden = true
        }
      },
      handleSortUpdated: () => {
        this._lowestCurrentPage = 1 // always reset to page 1 on sort change
        this._highestCurrentPage = 1
      },
      handleNextClick: e => {
        window.theme.tagalys.results.eventBus.publish(
          'results:ui:pagination:changed',
          {
            page: e.target.dataset.page,
          },
        )
      },
      handlePrevClick: e => {
        window.theme.tagalys.results.eventBus.publish(
          'results:ui:pagination:changed',
          {
            page: e.target.dataset.page,
          },
        )
      },
    }
  }

  connectedCallback () {
    this.subscribeToEvents()
    this.attachEventListeners()
  }

  disconnectedCallback () {
    this.unsubscribeFromEvents()
    this.detachEventListeners()
  }

  subscribeToEvents () {
    window.theme.tagalys.results.eventBus.subscribe(
      'results:state:products:updated',
      this._boundHandlers.handleProductsUpdated,
    )

    window.theme.tagalys.results.eventBus.subscribe(
      'results:ui:sort:changed',
      this._boundHandlers.handleSortUpdated,
    )
  }

  unsubscribeFromEvents () {
    window.theme.tagalys.results.eventBus.unsubscribe(
      'results:state:products:updated',
      this._boundHandlers.handleProductsUpdated,
    )

    window.theme.tagalys.results.eventBus.unsubscribe(
      'results:ui:sort:changed',
      this._boundHandlers.handleSortUpdated,
    )
  }

  attachEventListeners () {
    this.elements.nextTrigger?.addEventListener(
      'click',
      this._boundHandlers.handleNextClick,
    )
    this.elements.prevTrigger?.addEventListener(
      'click',
      this._boundHandlers.handlePrevClick,
    )
  }

  detachEventListeners () {
    this.elements.nextTrigger?.removeEventListener(
      'click',
      this._boundHandlers.handleNextClick,
    )
    this.elements.prevTrigger?.removeEventListener(
      'click',
      this._boundHandlers.handlePrevClick,
    )
  }

  render () {
    this.lowestCurrentPage = this.state.root.pagination.page
    this.highestCurrentPage = this.state.root.pagination.page

    const hasNextPage = window.theme.tagalys.results.SDK.helpers.hasNextPage()
    const hasPreviousPage = window.theme.tagalys.results.SDK.helpers.hasPreviousPage()

    if (this.elements.progressBar) {
      const progress =
        ((this.state.root.pagination.size * this.state.root.pagination.page) /
          this.state.root.pagination.total) *
        100
      this.elements.progressBar.style.setProperty(
        '--Transform_Progress',
        `${progress}%`,
      )
    }

    switch (this.dataset.type) {
      case 'previous':
        if (hasPreviousPage) {
          this.ariaHidden = this.lowestCurrentPage <= 1
          this.elements.prevTrigger.dataset.page = this.lowestCurrentPage - 1
        } else {
          this.ariaHidden = true
        }
        break
      case 'next':
        if (hasNextPage) {
          this.ariaHidden =
            this.state.root.pagination.size * this.highestCurrentPage >=
            this.state.root.pagination.total
          this.elements.nextTrigger.dataset.page = this.highestCurrentPage + 1
        } else {
          this.ariaHidden = true
        }
        break
      default:
        this.ariaHidden = !hasNextPage && !hasPreviousPage
    }
  }

  set lowestCurrentPage (page) {
    if (page === 0) return
    if (!this._lowestCurrentPage || page < this._lowestCurrentPage) {
      this._lowestCurrentPage = page
    }
  }

  get lowestCurrentPage () {
    return this._lowestCurrentPage || this.state.root.pagination.page - 1
  }

  set highestCurrentPage (page) {
    if (!this._highestCurrentPage || page > this._highestCurrentPage) {
      this._highestCurrentPage = page
    }
  }

  get highestCurrentPage () {
    return this._highestCurrentPage || this.state.root.pagination.page + 1
  }
}

customElements.define('pagination-change', PaginationChange)
